import FORM_AUDIENCE_TYPE from '../constants/FormAudienceType';
import FORM_ASSIGNMENT_TYPE from '../constants/FormAssignmentType';
import SELF_SERVICE_TEMPLATE_AUDIENCE from '../constants/selfServiceTemplateAudience';

// The form wizard's rules for which Admicity templates it offers for a form: the template's type
// against the form type, and the template's category and type against who the form is for. Every
// template follows them, whether it came with Admicity or a super admin made it on the template
// screen (AD-2537). The template screen reads the same rules to show who a template is offered to.

const normaliseText = (value) => String(value || '').trim().toLowerCase();

export const getNormalisedTemplateMeta = (template) => ({
    category: normaliseText(template?.category),
    parentCategory: normaliseText(template?.parentCategory),
    type: normaliseText(template?.type),
});

export const getTemplateCategoryNames = (template) => {
    const parentCategory = String(template?.parentCategory || '').trim();
    const category = String(template?.category || '').trim();

    if (!parentCategory) {
        return {
            category,
            subCategory: category,
        };
    }

    if (!category) {
        return {
            category: parentCategory,
            subCategory: parentCategory,
        };
    }

    if (
        normaliseText(parentCategory) === 'self service' &&
        normaliseText(category) === 'student admin'
    ) {
        return {
            category: 'Student Admin',
            subCategory: 'Student Admin',
        };
    }

    return {
        category: parentCategory,
        subCategory: category,
    };
};

export const wizardTemplateRules = ({
    formType,
    linkToStudents,
    linkToEmployees,
    parentAccountType,
    recipientTypeAudience,
    effectiveRecipientAssignmentTypeNum
}) => {
    // AD-2581: the self-service audience of the form being made, matched against a self-service template.
    const selfServiceAudience = (() => {
        if (formType !== 'selfService') return null;
        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
            return parentAccountType === 'with-account' ? SELF_SERVICE_TEMPLATE_AUDIENCE.parentWithAccount : null;
        }
        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.employees) {
            if (linkToStudents) return SELF_SERVICE_TEMPLATE_AUDIENCE.employeeLinkedToStudent;
            if (linkToEmployees) return SELF_SERVICE_TEMPLATE_AUDIENCE.employeeLinkedToEmployee;
            return SELF_SERVICE_TEMPLATE_AUDIENCE.employee;
        }
        return null;
    })();

    const filterAdmicityTemplateByFormType = (t) => {
        // A self-service template is offered to self-service forms only. One for children on roll only
        // (Medicine at School - New Version, Maria 8 October 2026) is offered to regular forms as well.
        if (normaliseText(t?.type) === 'selfservice') {
            return formType === 'selfService' || (Boolean(t?.onRollOnly) && ['email', 'noEmail'].includes(formType));
        }
        if (!formType) return true;

        if (formType === 'shareable') {
            return t.type === 'shareable';
        }

        if (formType === 'application') {
            return t.type === 'application';
        }

        if (formType === 'email' || formType === 'selfService' || formType === 'noEmail') {
            const blockedTypes = ['application', 'shareable'];
            return !blockedTypes.includes(t.type);
        }

        return true;
    };

    const isEmployeeTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        if (
            linkToStudents === true &&
            (
                parentCategory === 'staff' ||
                category === 'trips and activities'
            )
        ) {
            return false;
        }

        return (
            parentCategory === 'staff' ||
            category === 'staff admin' ||
            (category === 'trips' && parentCategory !== 'trips and activities') ||
            (!category && parentCategory === 'senior school admissions' && type === 'regular') ||
            (linkToStudents === true &&
                parentCategory === 'admissions' &&
                category === 'staff forms related to students' &&
                type === 'regular') ||
            (parentCategory === 'admissions' && [
                'boarding - staff',
                'sixth form - staff',
                'student admin - staff'
            ].includes(category))
        );
    };

    const isContactTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        return !parentCategory && category === 'student admin' && type === 'contact';
    };

    const isParentTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        return (
            (!parentCategory && ['medical', 'clubs', 'sen and welfare', 'senior school admissions'].includes(category)) ||
            (!parentCategory && category === 'student admin' && type === 'contact') ||
            (!parentCategory && category === 'transport' && type === 'regular') ||
            (parentCategory === 'senior school admissions' && category === 'transport' && type === 'regular') ||
            (parentCategory === 'student admin' && ['self service', 'transport', 'school meals', 'meals'].includes(category)) ||
            (parentCategory === 'student admin' && !category && type === 'contact') ||
            (parentCategory === 'medical' && ['self service', 'sen'].includes(category)) ||
            (parentCategory === 'trips and activities' && ['clubs', 'trips'].includes(category)) ||
            (parentCategory === 'admissions' && category === 'new starters' && ['regular', 'contact'].includes(type)) ||
            (parentCategory === 'admissions' && ['sixth form', 'sixth form - students'].includes(category) && type === 'regular') ||
            (parentCategory === 'admissions' && category === 'admissions' && type === 'contact')
        );
    };

    const isParentBeforeAcceptanceTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        return (
            (!parentCategory && category === 'admissions' && ['regular', 'offer'].includes(type)) ||
            (parentCategory === 'admissions' && [
                'boarding',
                'independent schools',
                'admissions',
                'offers and refusals',
                'new starters',
                'sixth form - students'
            ].includes(category) && type !== 'transition') ||
            (parentCategory === 'admissions' && category === 'sixth form' && ['offer', 'regular'].includes(type))
        );
    };

    const isPreviousSchoolTemplate = (template) => {
        const {
            type,
            category
        } = getNormalisedTemplateMeta(template);

        return type === 'transition' && category !== 'blank';
    };

    const isExternalTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);
        const hasExternalKeyword =
            normaliseText(category).includes('external') ||
            normaliseText(parentCategory).includes('external');

        if (effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.employees) {
            return false;
        }

        if (
            effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.students &&
            hasExternalKeyword
        ) {
            return true;
        }

        if (
            effectiveRecipientAssignmentTypeNum == null ||
            effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.none
        ) {
            return false;
        }

        if (effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.students) {
            return (
                (!parentCategory && category === 'student admin' && type === 'contact') ||
                (parentCategory === 'student admin' && !category && type === 'contact') ||
                (parentCategory === 'admissions' && category === 'independent schools - external forms' && type === 'regular') ||
                (parentCategory === 'trips and activities' && category === 'clubs - external' && type === 'regular') ||
                hasExternalKeyword
            );
        }

        return false;
    };

    const isStudentTemplate = (template) => {
        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        return (
            parentCategory === 'admissions' &&
            ['sixth form', 'sixth form - students'].includes(category) &&
            type === 'regular'
        );
    };

    const matchesTemplateAudience = (template) => {
        // A self-service template is offered to the audience chosen on it, whatever its category.
        if (normaliseText(template?.type) === 'selfservice') {
            if (template?.onRollOnly) {
                return recipientTypeAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
                    parentAccountType === 'with-account';
            }

            return selfServiceAudience !== null && (template.audiences ?? []).includes(selfServiceAudience);
        }

        const {
            category,
            parentCategory,
            type
        } = getNormalisedTemplateMeta(template);

        const isEnquiryAudience =
            recipientTypeAudience === FORM_AUDIENCE_TYPE.enquiries;
        const hasExternalKeyword =
            normaliseText(category).includes('external') ||
            normaliseText(parentCategory).includes('external');

        // AD-2674: a child on roll confirms a new place (Reception, Year 7, the sixth form) on an offer
        // template sent to Parent with an Admicity account, with a Single Choice in place of the
        // Application Status Choice. So offer templates suit every parent audience, and enquiries.
        const isParentAudience = recipientTypeAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;
        if (type === 'offer' && !isParentAudience && !isEnquiryAudience) {
            return false;
        }

        if (
            recipientTypeAudience === FORM_AUDIENCE_TYPE.externalUsers &&
            type === 'transition'
        ) {
            return false;
        }

        if (
            effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.students &&
            hasExternalKeyword
        ) {
            return true;
        }

        if (effectiveRecipientAssignmentTypeNum === FORM_ASSIGNMENT_TYPE.employees) {
            return false;
        }

        if (formType === 'selfService' && recipientTypeAudience === FORM_AUDIENCE_TYPE.employees) {
            // Maria, 12 October 2026: a Staff template such as Medical Information is a member of staff's form
            // about themselves, so it is never offered on a form about a student, as for regular forms above.
            const topLevelCategory = normaliseText(getTemplateCategoryNames(template).category);
            return topLevelCategory === 'staff' && linkToStudents !== true;
        }

        if (formType === 'selfService' && recipientTypeAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
            const {
                category,
                subCategory
            } = getTemplateCategoryNames(template);
            const topLevelCategory = normaliseText(category);
            const childCategory = normaliseText(subCategory);
            const blockedSelfServiceParentCategories = [
                'admissions',
                'clubs',
                'senior school admissions',
            ];

            if (blockedSelfServiceParentCategories.includes(topLevelCategory)) {
                return false;
            }

            const isMedicalChildCategory = childCategory === 'medical';
            const isTopMedicalSelfService =
                topLevelCategory === 'medical' &&
                childCategory === 'self service';

            if (isMedicalChildCategory && !isTopMedicalSelfService) {
                return false;
            }
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.externalUsers) {
            return isExternalTemplate(template);
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
            if (['without', 'applicant-status'].includes(parentAccountType)) {
                return isParentBeforeAcceptanceTemplate(template) || isContactTemplate(template);
            }

            if (['with', 'with-account', 'without-account'].includes(parentAccountType)) {
                return isParentTemplate(template) || type === 'offer';
            }

            return isParentTemplate(template) || isParentBeforeAcceptanceTemplate(template);
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.enquiries) {
            return isParentBeforeAcceptanceTemplate(template);
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.employees) {
            return isEmployeeTemplate(template);
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.previousSchool) {
            return isPreviousSchoolTemplate(template);
        }

        if (recipientTypeAudience === FORM_AUDIENCE_TYPE.students) {
            return isStudentTemplate(template);
        }

        return true;
    };

    return {
        filterAdmicityTemplateByFormType,
        matchesTemplateAudience
    };
};

// The form types a template of each kind can be used for. The same rules apply to Admicity templates and
// to a school's own templates.
export const templateFitsFormType = (template, formType) => {
    const type = normaliseText(template?.type);

    // A trigger form can be built from any template; its audience is checked separately.
    if (!formType || formType === 'triggerForm') return true;

    // A self-service template is for self-service forms only. One for children on roll only
    // (Medicine at School - New Version) is offered to regular forms as well.
    if (type === 'selfservice') {
        return formType === 'selfService' || (Boolean(template?.onRollOnly) && ['email', 'noEmail'].includes(formType));
    }

    if (formType === 'shareable') return type === 'shareable';
    if (formType === 'application') return type === 'application';

    return !['application', 'shareable'].includes(type);
};

// AD-2689 (Maria, 14 October 2026): nobody can choose a form type or audience that the form's template was
// not made for, in the Create Form box or in Form Settings. A blank form fits everything. The audience is
// checked by the same rules the template list uses.
export const isTemplateCompatible = ({
    template,
    formType,
    audience,
    parentAccountType,
    linkToStudents,
    linkToEmployees,
    assignmentType,
}) => {
    if (!template || getNormalisedTemplateMeta(template).category === 'blank') return true;
    if (!templateFitsFormType(template, formType)) return false;
    if (audience == null) return true;

    const {matchesTemplateAudience} = wizardTemplateRules({
        formType,
        linkToStudents,
        linkToEmployees,
        parentAccountType,
        recipientTypeAudience: audience,
        effectiveRecipientAssignmentTypeNum: assignmentType ?? null,
    });

    return matchesTemplateAudience(template);
};
